import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useFormik, FormikProvider } from 'formik'
import { AxiosError } from 'axios'
import * as yup from 'yup'
import { useNavigateWithPersistParams } from '@/utils/sharedHooks'
import { SIGNUP_PATHS } from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { Text, Image, Form, Alert } from '@/ui'
import { TypographyStyles, TextColors } from '@/styles'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { makeReqEmailSchema, getFieldName } from '@/base'
import { emailCapture, EmailCapturePayload } from '@/actions/authActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { logHeardError } from '@/utils/errorHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import { SignupFormFieldsWrapper } from '../SignupFormFieldsWrapper'

const validationSchema = yup.object({
  email: makeReqEmailSchema({ field: 'email' }),
})

export const EmailCapture = () => {
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const navigate = useNavigateWithPersistParams()
  const consult = searchParams.get('consult')
  const freeTrialPromoCode = searchParams.get('trial')
  const [emailExists, setEmailExists] = useState(false)
  const track = useAnalyticsTrack()

  const freeTrialEnabled = useFreeTrialEnabled(freeTrialPromoCode)

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: async (values) => {
      try {
        track('clicked email capture continue button')

        if (emailExists) {
          setEmailExists(false)
        }
        const payload: EmailCapturePayload = {
          email: values.email,
          hadConsult: consult === 'true',
        }
        if (freeTrialPromoCode) {
          payload.freeTrialPromoCode = freeTrialPromoCode
        }
        await dispatch(emailCapture(payload))
      } catch (err) {
        logHeardError(err)
        if (err instanceof AxiosError) {
          const emailExists = err.response?.data?.emailExists
          if (emailExists) {
            setEmailExists(true)
            return
          }
        }
      }
      sessionStorage.setItem('signupEmail', values.email)
      navigate(SIGNUP_PATHS.aboutYou)
    },
  })

  const { submitForm, isSubmitting } = formik

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onContinue={submitForm}
          continueDisabled={isSubmitting}
          continueLoading={isSubmitting}
          continueId="btn-signup-create-account"
        />
      }
    >
      {freeTrialEnabled && (
        <div className="w-full mb-8">
          <FreeTrialSignupBanner />
        </div>
      )}

      {emailExists && (
        <div className="w-full mb-8">
          <Alert
            variant="error"
            description="It looks like an account with this email exists already."
          />
        </div>
      )}

      <section className="flex flex-col md:flex-row justify-between gap-14">
        <SignupFormFieldsWrapper>
          <div className="w-full mb-4">
            <Text variant={TypographyStyles.DISPLAY_MD}>Get started today</Text>
          </div>
          <div className="w-full mb-8">
            <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
              We&apos;re excited to help you simplify your finances. Your email
              allows us to reach out in case you need assistance.
            </Text>
          </div>
          <FormikProvider value={formik}>
            <Form.Input
              name={getFieldName<typeof formik.values>('email')}
              label="Business Email Address"
              placeholder="Business email"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitForm()
                }
              }}
            />
          </FormikProvider>
        </SignupFormFieldsWrapper>
        {!isMobile && (
          <div className="flex justify-center rounded-2xl bg-yellow-25 pt-6 px-6 w-[300px] shrink-0 self-start">
            <Image
              src="https://heard-images.s3.amazonaws.com/assets/computer-desk.svg"
              alt="computer desk"
              objectFit="contain"
              className="w-[180px]"
            />
          </div>
        )}
      </section>
    </SignupPageLayout>
  )
}
